import   React                      ,
       { useMemo                    ,
         useState                   }  from 'react';

import   classNames                    from 'classnames';

import   styles                        from './VideoResolutionSelector.module.scss';

import   Toggle                        from '../../widget/inputs/Toggle';
import   Trigger                       from '../../widget/actuators/Trigger';

import { COMPRESSION_LEVELS         ,
         CompressionLevel           ,
         DEFAULT_COMPRESSION_LEVEL  ,
         HlsResolutionId            ,
         estimateHlsSizeBytes       ,
         formatEstimatedSize        ,
         getCompressionLevelInfo    ,
         maxHlsSizeBytes            ,
         selectableHlsProfiles      }  from '../../../dataTypes/mediaManagment/hlsResolutionProfiles';

import { BUY_STORAGE_LABEL         ,
         openBuyStorage            }  from '../../../utils/utils_storagePurchase';

// Qualité toujours générée : cochée d'office, et sa case ne se décoche pas. Les qualités
// réduites ne servent qu'en complément, pour les spectateurs au débit trop faible.
const MANDATORY_RESOLUTION: HlsResolutionId = '1080p';

const MAX_LEVEL = COMPRESSION_LEVELS.length - 1;

export interface VideoResolutionSelectorProps {
  durationSeconds     : number;
  quotaRemainingBytes : number;
  initialSelected?    : HlsResolutionId[];
  initialCompression? : CompressionLevel;
  source4K?           : boolean;   // la source atteint la 4K : le 2160p est proposé, jamais coché d'office
  onConfirm           : (selected: HlsResolutionId[], compression: CompressionLevel) => void;
  onCancel            : () => void;
}

// Jauge à quatre crans : `value` crans allumés.
const Pips = ({ value }: { value: number }) => (
  <span className={classNames(styles.pips)} aria-hidden="true">
    {[1, 2, 3, 4].map(n => <span key={n} className={classNames(styles.pip, n <= value && styles.pipOn)} />)}
  </span>
);

export const VideoResolutionSelector = ({ durationSeconds     ,
                                           quotaRemainingBytes ,
                                           initialSelected     = [] ,
                                           initialCompression  = DEFAULT_COMPRESSION_LEVEL ,
                                           source4K            = false ,
                                           onConfirm           ,
                                           onCancel            }: VideoResolutionSelectorProps) => {

  const [ selected   , setSelected    ] = useState<HlsResolutionId[]>(
    initialSelected.includes(MANDATORY_RESOLUTION) ? initialSelected : [MANDATORY_RESOLUTION, ...initialSelected]
  );
  const [ compression, setCompression ] = useState<CompressionLevel>(initialCompression);

  const levelInfo = getCompressionLevelInfo(compression);
  const profiles  = useMemo(() => selectableHlsProfiles(source4K), [source4K]);

  const estimates = useMemo(
    () => profiles.map(profile => ({
            profile ,
            sizeBytes : estimateHlsSizeBytes(profile, durationSeconds, compression) ,
          })),
    [profiles, durationSeconds, compression]
  );

  const totalSizeBytes = useMemo(
    () => estimates
            .filter(({ profile }) => selected.includes(profile.id))
            .reduce((sum, { sizeBytes }) => sum + sizeBytes, 0),
    [estimates, selected]
  );

  // Borne haute : le débit colle au plafond de chaque qualité sur toute la durée.
  const worstCaseBytes = useMemo(
    () => profiles
            .filter(profile => selected.includes(profile.id))
            .reduce((sum, profile) => sum + maxHlsSizeBytes(profile, durationSeconds), 0),
    [profiles, selected, durationSeconds]
  );

  const quotaExceeded  = totalSizeBytes > quotaRemainingBytes;
  const worstCaseAlert = !quotaExceeded && worstCaseBytes > quotaRemainingBytes;

  const handleToggle = (checked: boolean, resolutionId: HlsResolutionId) => {
    if (resolutionId === MANDATORY_RESOLUTION) return;
    setSelected(prev => checked
      ? [...prev, resolutionId].filter((value, index, self) => self.indexOf(value) === index)
      : prev.filter(id => id !== resolutionId)
    );
  };

  const handleConfirmClick = () => {
    if (selected.length === 0 || quotaExceeded) return;
    onConfirm(selected, compression);
  };

  const fill = (compression / MAX_LEVEL) * 100;

  return (
    <div className={classNames(styles.root)}>

      <div className={classNames(styles.header)}>
        <div className={classNames(styles.title)}>Qualités à générer</div>
        <div className={classNames(styles.subtitle)}>Choisissez les formats à produire et le niveau de compression.</div>
      </div>

      {/* ── Formats ─────────────────────────────────────────────────────────── */}
      <div className={classNames(styles.section)}>
        <div className={classNames(styles.sectionTitle)}>Formats</div>

        <div className={classNames(styles.list)}>
          {estimates.map(({ profile, sizeBytes }) => {
            const impossible = sizeBytes > quotaRemainingBytes;
            const mandatory  = profile.id === MANDATORY_RESOLUTION;
            const active     = selected.includes(profile.id);

            return (
              <div key={profile.id} className={classNames(styles.row, active && styles.rowActive)}>
                <span className = {classNames(mandatory && styles.locked)}
                      title     = {mandatory ? 'Qualité toujours générée' : undefined}>
                  <Toggle checked  = {active}
                          readOnly = {impossible || mandatory}
                          onChange = {(checked) => handleToggle(checked, profile.id)} />
                </span>

                <div className={classNames(styles.label)}>
                  {profile.label}
                  {mandatory && <span className={classNames(styles.badge)}>toujours générée</span>}
                </div>

                <div className={classNames(styles.size, impossible && styles.impossible)}>
                  ≈ {formatEstimatedSize(sizeBytes)}
                  {impossible && <span className={classNames(styles.impossibleText)}> — espace insuffisant</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Réglage compression ─────────────────────────────────────────────── */}
      <div className={classNames(styles.section)}>
        <div className={classNames(styles.sectionTitle)}>Réglage compression</div>

        <div className={classNames(styles.slider)}>
          <div className={classNames(styles.endLabels)}>
            <span>Qualité maximale</span>
            <span>Place minimale</span>
          </div>

          <input type            = "range"
                 min             = {0}
                 max             = {MAX_LEVEL}
                 step            = {1}
                 value           = {compression}
                 onChange        = {(event) => setCompression(Number(event.target.value) as CompressionLevel)}
                 className       = {classNames(styles.range)}
                 style           = {{ '--fill': `${fill}%` } as React.CSSProperties}
                 aria-label      = "Réglage de compression"
                 aria-valuetext  = {`${levelInfo.title} — qualité ${levelInfo.quality}, stockage ${levelInfo.storage}`} />

          <div className={classNames(styles.ticks)}>
            {COMPRESSION_LEVELS.map(info => (
              <button key       = {info.level}
                      type      = "button"
                      className = {classNames(styles.tick, info.level === compression && styles.tickActive)}
                      style     = {{ '--pos': info.level / MAX_LEVEL } as React.CSSProperties}
                      onClick   = {() => setCompression(info.level)}>
                <span className={classNames(styles.tickNumber)}>{info.level}</span>
                <span className={classNames(styles.tickLabel)}>{info.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={classNames(styles.levelCard)} aria-live="polite">
          <div className={classNames(styles.levelTitle)}>{levelInfo.title}</div>
          <div className={classNames(styles.meters)}>
            <div className={classNames(styles.meter)}>
              <span className={classNames(styles.meterName)}>Qualité</span>
              <Pips value={levelInfo.qualityPips} />
              <span className={classNames(styles.meterValue)}>{levelInfo.quality}</span>
            </div>
            <div className={classNames(styles.meter)}>
              <span className={classNames(styles.meterName)}>Stockage</span>
              <Pips value={levelInfo.storagePips} />
              <span className={classNames(styles.meterValue)}>{levelInfo.storage}</span>
            </div>
          </div>
        </div>
      </div>

      <p className={classNames(styles.info)}>
        Estimation indicative : la taille réelle dépend du contenu (un plan fixe pèse bien moins qu'une scène
        très animée). En cas de problème de débit, générez aussi un format réduit (utilise plus de place de stockage).
      </p>

      {selected.includes('2160p') && (
        <p className={classNames(styles.info)}>
          La 4K s'encode nettement plus lentement et occupe beaucoup de place. Certains téléviseurs et
          téléphones ne la lisent pas : la qualité 1080p reste générée pour eux.
        </p>
      )}

      {/* ── Total ───────────────────────────────────────────────────────────── */}
      <div className={classNames(styles.total)}>
        <span>Total estimé :</span>
        <span className={classNames(quotaExceeded && styles.impossibleText)}>
          ≈ {formatEstimatedSize(totalSizeBytes)} / {Number.isFinite(quotaRemainingBytes) ? formatEstimatedSize(quotaRemainingBytes) + ' disponibles' : 'espace illimité'}
        </span>
      </div>

      {worstCaseAlert && (
        <p className={classNames(styles.warn)}>
          Sur une vidéo très chargée en mouvement, la taille peut monter jusqu'à {formatEstimatedSize(worstCaseBytes)},
          au-delà de l'espace disponible.
        </p>
      )}

      {quotaExceeded && (
        <p className={classNames(styles.alert)}>
          La sélection dépasse l'espace disque disponible : décochez une qualité ou choisissez une compression plus forte.
        </p>
      )}

      {selected.length === 0 && !quotaExceeded && (
        <p className={classNames(styles.alert)}>Choisissez au moins une qualité.</p>
      )}

      <div className={classNames(styles.buttons)}>
        <div className={classNames(styles.buyStorage)}>
          <Trigger name={BUY_STORAGE_LABEL} onClick={() => openBuyStorage()} variant="tailwind2" />
        </div>
        <Trigger name="Annuler" onClick={onCancel} variant="tailwind2" />
        <Trigger name="Valider" onClick={handleConfirmClick} variant="tailwind2" />
      </div>

    </div>
  );
};

export default VideoResolutionSelector;
